import { H3, Stack, Text, Box, Dropdown } from "@adfenix/alexandria";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FacebookIntegration, getOfficesByHqId } from "api/services";
import { Switch } from "components/Switch/Switch";
import { useQuery } from "@tanstack/react-query";
import { getOfficesFacebookIntegration } from "api/services/facebookService";
import { IDataTableColumn } from "react-data-table-component";
import { Table } from "components/Datatable";
import ResultsNoData from "components/NoData";
import { StyledLinkButton } from "./StyledElements";

interface OfficeOverview {
  name: string;
  id: string;
  facebookPageId: string | undefined;
  facebookPageName: string | undefined;
  facebookUserName: string | undefined;
  facebookPageUrl: string | undefined;
  instagramPageName: string | undefined;
  instagramPageUrl: string | undefined;
  connected: boolean;
}

const OfficeOverview = ({
  hqIntegration,
  hqId
}: {
  hqIntegration: FacebookIntegration;
  hqId: string;
}) => {
  const { t } = useTranslation();
  const [allData, setAllData] = useState<OfficeOverview[]>([]);
  const [officesOverviewData, setOfficesOverviewData] = useState<
    OfficeOverview[]
  >([]);
  const [loading, setLoading] = useState(true);

  const { data: officesConfig, refetch } = useQuery(
    ["getOfficesByHqId", hqId],
    () => getOfficesByHqId(hqId)
  );

  const { refetch: refetchIntegrations, data } = useQuery(
    ["getOfficesFacebookIntegration", hqId],
    () => getOfficesFacebookIntegration(hqId)
  );

  useEffect(() => {
    if (!data) {
      return;
    }

    const activeOffice = officesConfig?.filter(m => m.active == true);
    if (activeOffice && activeOffice.length > 0) {
      const officesOverview: OfficeOverview[] = activeOffice.map(office => {
        const integration = data.find(m => m.subjectId == office.id);
        return {
          id: office.id,
          facebookPageName: integration?.facebookPageName,
          facebookPageId: integration?.facebookPageId,
          name: office.name,
          connected: integration?.facebookPageId ? true : false,
          facebookPageUrl: integration?.facebookPageId
            ? `https://facebook.com/${integration?.facebookPageId}`
            : undefined,
          instagramPageName: integration?.instagramAccountName,
          instagramPageUrl: integration?.instagramAccountName
            ? `https://instagram.com/${integration?.instagramAccountName}`
            : undefined,
          facebookUserName:
            integration?.facebookUserName ?? integration?.facebookPageName
        };
      });
      setOfficesOverviewData(officesOverview);
      setAllData(officesOverview);
    }
    setLoading(false);
  }, [data, officesConfig]);

  useEffect(() => {
    if (officesConfig && officesConfig.length > 0) {
      refetchIntegrations();
    } else {
      setLoading(false);
    }
  }, [officesConfig, refetchIntegrations]);

  useEffect(() => {
    if (hqId) {
      setLoading(true);
      refetch();
      setOfficesOverviewData([]);
      setAllData([]);
    }
  }, [setOfficesOverviewData, setLoading, hqId, refetch]);

  const PageConnection = ({ name, url }: { name: string; url: string }) => {
    return (
      <Box display="flex">
        <StyledLinkButton
          logLabel="office-facebook-page-link-button"
          onClick={() => window.open(url, "_blank")}
          variant="text"
        >
          {name?.replace(/(.{25})..+/, "$1...")}
        </StyledLinkButton>
      </Box>
    );
  };

  const columns: IDataTableColumn[] = [
    {
      selector: "name",
      name: t("selfonboarding.officeOverview.table.header.office"),
      sortable: true,
      cell: (row: OfficeOverview) => <Text>{row.name}</Text>
    },
    {
      selector: "facebookPageName",
      name: t("selfonboarding.officeOverview.table.header.facebookPage"),
      sortable: true,
      cell: (row: OfficeOverview) =>
        row.facebookPageId ? (
          <PageConnection
            name={row.facebookUserName ?? ""}
            url={row.facebookPageUrl ?? ""}
          />
        ) : (
          <Text style={{ fontStyle: "italic", color: "gray" }}>
            {t("selfonboarding.pageItem.noConnection.label")}
          </Text>
        )
    },
    {
      selector: "instagramPageName",
      name: t("selfonboarding.officeOverview.table.header.instagram"),
      sortable: true,
      cell: (row: OfficeOverview) =>
        row.instagramPageName ? (
          <PageConnection
            name={row.instagramPageName ?? ""}
            url={row.instagramPageUrl ?? ""}
          />
        ) : (
          <Text style={{ fontStyle: "italic", color: "gray" }}>
            {t("selfonboarding.pageItem.noConnection.label")}
          </Text>
        )
    }
  ];
  return (
    <Stack spacing="xs2">
      <H3>{t("selfonboarding.officeOverview.header")}</H3>
      <Box display="flex">
        <Box justifyContent="center">
          <Switch
            checked={hqIntegration.allowOfficeUsersToConfigureSettings}
            disabled={true}
          />
        </Box>
        <Box style={{ marginTop: 24, marginLeft: 16 }}>
          <Text>
            {hqIntegration.allowOfficeUsersToConfigureSettings
              ? t("selfonboarding.officeOverview.allowOffices.label")
              : t("selfonboarding.officeOverview.notAllowOffices.label")}
          </Text>
        </Box>
      </Box>

      <hr />
      <Box style={{ marginBottom: 24 }}>
        <Text>{t("selfonboarding.officeOverview.table.description")}</Text>
      </Box>

      <Dropdown
        style={{ width: 280 }}
        items={[
          t("selfonboarding.officeOverview.filter.all"),
          t("selfonboarding.officeOverview.filter.withConnections"),
          t("selfonboarding.officeOverview.filter.withoutConnections")
        ]}
        label={"Filter by"}
        defaultValue={t("selfonboarding.officeOverview.filter.all").toString()}
        onChange={type => {
          switch (type) {
            case t("selfonboarding.officeOverview.filter.all").toString():
              setOfficesOverviewData(allData);
              break;

            case t(
              "selfonboarding.officeOverview.filter.withConnections"
            ).toString():
              setOfficesOverviewData(allData.filter(x => x.connected == true));
              break;

            case t(
              "selfonboarding.officeOverview.filter.withoutConnections"
            ).toString():
              setOfficesOverviewData(allData.filter(x => x.connected == false));
              break;
          }
        }}
      />

      <Box pY="s">
        <Table
          style={{ width: "100%" }}
          dense
          progressPending={loading}
          defaultSortAsc={false}
          columns={columns}
          data={officesOverviewData ?? []}
          noDataComponent={<ResultsNoData />}
          pagination={true}
        />
      </Box>
    </Stack>
  );
};

export default OfficeOverview;
